import React, { useState, useEffect } from 'react';
import { Palette, Save, RotateCcw } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { brandingAPI } from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import '../../styles/pages/BrandingPage.css';
import '../../styles/admin-shared.css';

const SUPPORTED_PAGES = [
  { key: 'home', label: 'Home', description: 'Página inicial' },
  { key: 'progress', label: 'Progresso', description: 'Página de progresso' },
  { key: 'community', label: 'Comunidade', description: 'Página da comunidade' },
  { key: 'challenges', label: 'Desafios', description: 'Página de desafios' },
  { key: 'diary', label: 'Diário', description: 'Página do diário' },
  { key: 'chats', label: 'Conversas', description: 'Página de conversas' },
  { key: 'dashboard', label: 'Dashboard', description: 'Dashboard principal' },
  { key: 'resources', label: 'Recursos', description: 'Página de recursos' },
  { key: 'blockerSettings', label: 'Bloqueador - Configurações', description: 'Configurações do bloqueador' },
  { key: 'blockerLogs', label: 'Bloqueador - Logs', description: 'Logs do bloqueador' },
  { key: 'blockerStats', label: 'Bloqueador - Estatísticas', description: 'Estatísticas do bloqueador' },
  { key: 'meditation', label: 'Meditação', description: 'Página de meditação' },
  { key: 'emergency', label: 'Emergência', description: 'Página de emergência' },
];

const DEFAULT_COLORS = {
  primaryBackground: '#10083B',
  secondaryBackground: '#242442',
  primary: '#1A95FF',
  success: '#20FF80',
  danger: '#FF4545',
  textPrimary: '#FAFAFA',
  textSecondary: '#CFCFCF',
};

const BrandingPage = () => {
  // Re-fetch on program switch — branding is per-program
  const { slug } = useProgram();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    colors: DEFAULT_COLORS,
    backgrounds: {},
    background_opacity: {},
    logo_url: null,
    icon_url: null,
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewColors, setPreviewColors] = useState(DEFAULT_COLORS);

  useEffect(() => {
    loadBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const response = await brandingAPI.get();
      if (response.data.success) {
        const data = response.data.data;
        setBranding({
          colors: data.colors || DEFAULT_COLORS,
          backgrounds: data.backgrounds || {},
          background_opacity: data.background_opacity || {},
          logo_url: data.logo_url,
          icon_url: data.icon_url,
        });
        setPreviewColors(data.colors || DEFAULT_COLORS);
      }
    } catch (error) {
      console.error('Erro ao carregar branding:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações de branding' });
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (key, value) => {
    const newColors = { ...branding.colors, [key]: value };
    setBranding({ ...branding, colors: newColors });
    setPreviewColors(newColors);
  };

  const handleOpacityChange = (page, value) => {
    const newOpacity = { ...branding.background_opacity, [page]: parseInt(value) };
    setBranding({ ...branding, background_opacity: newOpacity });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await brandingAPI.update({
        colors: branding.colors,
        background_opacity: branding.background_opacity,
      });

      setMessage({ type: 'success', text: 'Branding salvo com sucesso!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Erro ao salvar branding:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao salvar branding',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(png|jpeg|jpg|webp|svg\+xml)/)) {
      setMessage({ type: 'error', text: 'Apenas arquivos PNG, JPG, WebP ou SVG são permitidos' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Arquivo muito grande. Máximo: 5MB' });
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('logo', file);

      const response = await brandingAPI.uploadLogo(formData);
      if (response.data.success) {
        setBranding({ ...branding, logo_url: response.data.data.logo_url });
        setMessage({ type: 'success', text: 'Logo enviado com sucesso!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao fazer upload do logo',
      });
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(png|jpeg|jpg|webp|svg\+xml)/)) {
      setMessage({ type: 'error', text: 'Apenas arquivos PNG, JPG, WebP ou SVG são permitidos' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Arquivo muito grande. Máximo: 2MB' });
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('icon', file);

      const response = await brandingAPI.uploadIcon(formData);
      if (response.data.success) {
        setBranding({ ...branding, icon_url: response.data.data.icon_url });
        setMessage({ type: 'success', text: 'Ícone enviado com sucesso!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Erro ao fazer upload do ícone:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao fazer upload do ícone',
      });
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  const handleBackgroundUpload = async (page, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(png|jpeg|jpg|webp)/)) {
      setMessage({ type: 'error', text: 'Apenas arquivos PNG, JPG ou WebP são permitidos' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Arquivo muito grande. Máximo: 5MB' });
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('background', file);

      const response = await brandingAPI.uploadBackground(page, formData);
      if (response.data.success) {
        const newBackgrounds = { ...branding.backgrounds, [page]: response.data.data.background_url };
        setBranding({ ...branding, backgrounds: newBackgrounds });
        setMessage({ type: 'success', text: `Background de ${SUPPORTED_PAGES.find(p => p.key === page)?.label} enviado com sucesso!` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Erro ao fazer upload do background:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao fazer upload do background',
      });
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  const handleRemoveBackground = async (page) => {
    if (!confirm(`Tem certeza que deseja remover o background de ${SUPPORTED_PAGES.find(p => p.key === page)?.label}?`)) {
      return;
    }

    try {
      setSaving(true);
      await brandingAPI.removeBackground(page);
      const newBackgrounds = { ...branding.backgrounds };
      delete newBackgrounds[page];
      setBranding({ ...branding, backgrounds: newBackgrounds });
      setMessage({ type: 'success', text: 'Background removido com sucesso!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Erro ao remover background:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao remover background',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Tem certeza que deseja resetar todas as configurações de branding para os valores padrão? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setSaving(true);
      await brandingAPI.reset();
      await loadBranding();
      setMessage({ type: 'success', text: 'Branding resetado para valores padrão!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Erro ao resetar branding:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao resetar branding',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="branding-page">
          <div className="loading">Carregando...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="branding-page">
        <div className="page-header">
          <h1><Palette size={26} /> Branding</h1>
          <p>Configure cores, logo, ícone e backgrounds personalizados para o app</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="branding-container">
          {/* Preview ao vivo */}
          <div className="preview-section">
            <h2>Preview ao Vivo</h2>
            <div className="preview-card" style={{ backgroundColor: previewColors.secondaryBackground }}>
              <div className="preview-header" style={{ backgroundColor: previewColors.primaryBackground }}>
                <div className="preview-logo">
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Logo" />
                  ) : (
                    <div className="preview-logo-placeholder">Logo</div>
                  )}
                </div>
              </div>
              <div className="preview-content">
                <h3 style={{ color: previewColors.textPrimary }}>Título de Exemplo</h3>
                <p style={{ color: previewColors.textSecondary }}>Texto secundário de exemplo</p>
                <button
                  className="preview-button"
                  style={{
                    backgroundColor: previewColors.primary,
                    color: previewColors.textPrimary,
                  }}
                >
                  Botão Primário
                </button>
              </div>
            </div>
          </div>

          {/* Paleta de Cores */}
          <div className="colors-section">
            <h2>Paleta de Cores</h2>
            <div className="colors-grid">
              {Object.entries(DEFAULT_COLORS).map(([key, defaultValue]) => (
                <div key={key} className="color-input-group">
                  <label>{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                  <div className="color-input-wrapper">
                    <input
                      type="color"
                      value={branding.colors[key] || defaultValue}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.colors[key] || defaultValue}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Logo e Ícone */}
          <div className="logo-section">
            <h2>Logo e Ícone</h2>
            <div className="logo-grid">
              <div className="logo-card">
                <h3>Logotipo</h3>
                {branding.logo_url && (
                  <div className="logo-preview">
                    <img src={branding.logo_url} alt="Logo" />
                  </div>
                )}
                <label className="upload-button">
                  {branding.logo_url ? 'Alterar Logo' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                <p className="upload-hint">PNG, SVG, JPG ou WebP. Máx: 5MB</p>
              </div>

              <div className="logo-card">
                <h3>Ícone</h3>
                {branding.icon_url && (
                  <div className="icon-preview">
                    <img src={branding.icon_url} alt="Icon" />
                  </div>
                )}
                <label className="upload-button">
                  {branding.icon_url ? 'Alterar Ícone' : 'Upload Ícone'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleIconUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                <p className="upload-hint">PNG, SVG, JPG ou WebP. Máx: 2MB</p>
              </div>
            </div>
          </div>

          {/* Backgrounds por Página */}
          <div className="backgrounds-section">
            <h2>Backgrounds por Página</h2>
            <p className="section-description">
              Configure imagens de background personalizadas para cada página. Tamanho recomendado: 1920x1080px (web) ou 1080x1920px (mobile). Máx: 5MB
            </p>
            <div className="backgrounds-grid">
              {SUPPORTED_PAGES.map((page) => (
                <div key={page.key} className="background-card">
                  <h3>{page.label}</h3>
                  <p className="page-description">{page.description}</p>
                  
                  {branding.backgrounds[page.key] ? (
                    <div className="background-preview">
                      <img src={branding.backgrounds[page.key]} alt={page.label} />
                      <button
                        className="remove-button"
                        onClick={() => handleRemoveBackground(page.key)}
                        disabled={saving}
                      >
                        Remover
                      </button>
                    </div>
                  ) : (
                    <div className="background-placeholder">
                      <span>Sem background</span>
                    </div>
                  )}

                  <label className="upload-button-small">
                    {branding.backgrounds[page.key] ? 'Alterar Background' : 'Upload Background'}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={(e) => handleBackgroundUpload(page.key, e)}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {branding.backgrounds[page.key] && (
                    <div className="opacity-control">
                      <label>
                        Opacidade: {branding.background_opacity[page.key] || 100}%
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={branding.background_opacity[page.key] || 100}
                          onChange={(e) => handleOpacityChange(page.key, e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ações */}
          <div className="actions-section">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Salvando...' : <><Save size={16} /> Salvar Branding</>}
            </button>
            <button
              className="btn btn-danger"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw size={16} /> Resetar para Padrão
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BrandingPage;

